import { Meta } from '../Meta';
import { MarketingPage } from '../marketing/MarketingPage';
import React from 'react';

export const SectionHeader = ({ title, children }) => (
  <div className="mb-4">
    <h2 className="font-sans text-2xl md:text-3xl font-medium text-fog-900 mb-6">
      {title}
    </h2>
    <div className="font-sans text-lg md:text-xl text-fog-900">
      {children}
    </div>
  </div>
);

export const CaseStudySection = ({ children }) => (
  <div className="w-full bg-lupine-50 overflow-hidden pt-4 pb-4 md:pt-6 md:pb-6">
    <div className="max-w-screen-md mx-auto px-4 md:max-w-screen-lg md:px-8">
      {children}
    </div>
  </div>
);

export const CaseStudyHeaderSection = () => (
  <div className="w-full bg-gradient-to-br from-lupine-950 via-lupine-800 to-lupine-600 overflow-hidden pt-12 pb-12 md:pt-24 md:pb-24">
    <div className="max-w-screen-xl mx-auto px-4 md:grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 items-center gap-8">
      <div className="md:col-span-2 lg:col-span-3 order-1 md:order-2 mb-8 shadow-xl rounded-lg hover:shadow-2xl transition-all">
        <img
          src="/images/case-studies/juicebox_x_wolfia.png"
          alt="Juicebox x Wolfia"
          className="w-full h-auto object-cover rounded-lg"
        />
      </div>

      <div className="md:col-span-1 lg:col-span-2 order-2 md:order-1">
        <div className="mb-4 text-center md:text-left">
          <p className="text-md md:text-lg text-plum-100">
            Case Study
          </p>
          <h1 className="text-3xl md:text-5xl font-medium text-plum-50 tracking-tight leading-snug mt-4 mb-8">
            How Juicebox closes deals faster with the Wolfia AI Trust Center
          </h1>
        </div>
        <div className="text-center md:text-left flex flex-col gap-4 mb-6">
          <p className="text-lg text-plum-50 leading-10">
            <span className="text-lg text-pineglade-300 uppercase font-semibold">Company</span>Juicebox (PeopleGPT)
          </p>
          <p className="text-lg text-plum-50 leading-10">
            <span className="text-lg text-pineglade-300 uppercase font-semibold">Description</span>AI-powered people search
          </p>
          <p className="text-lg text-plum-50 leading-10">
            <span className="text-lg text-pineglade-300 uppercase font-semibold">Headquarters</span>San Francisco, CA
          </p>
        </div>
        <div className="text-center md:text-left">
          <a
            href="https://juicebox.wolfia.com/"
            className="inline-flex text-plum-50 bg-plum-500 rounded-full items-center py-4 px-6 drop-shadow-xl hover:drop-shadow-2xl hover:text-white text-lg transition-all focus-visible:outline outline-pineglade-500 outline-offset-4 outline-4 font-semibold"
          >
            Visit trust center
          </a>
        </div>
      </div>
    </div>
  </div>
);


export const CaseStudyCustomer = ({ quote, author, role, image }) => (
  <div className="w-full bg-lupine-100 overflow-hidden pt-12 pb-12">
    <div className="max-w-5xl mx-auto px-4">
      <div className="flex justify-center mb-8">
        <img
          src="/images/logos/companies/juicebox.svg"
          alt="Juicebox logo"
          className="h-12 w-auto"
        />
      </div>
      <div className="text-center text-lg md:text-xl font-semibold text-fog-900 mb-8">
        5,000 views of the AI Trust Center within 2 weeks since launch
      </div>
      <blockquote className="text-center text-lg md:text-xl text-fog-900 px-6 md:px-0">
        {quote}
      </blockquote>
      <div className="flex flex-col items-center justify-center gap-4 mt-8">
        <div className="w-24 h-24 md:w-28 md:h-28 overflow-hidden rounded-full">
          <img
            src={image}
            alt={`Portrait of ${author}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="text-center">
          <p className="text-lg md:text-xl font-semibold">
            {author}
          </p>
          <p className="text-md md:text-lg">
            {role}
          </p>
        </div>
      </div>
    </div>
  </div>
);

export const CaseStudyContentSection = ({ title, points }) => (
  <CaseStudySection>
    <SectionHeader title={title}>
      <ul className="list-disc list-outside pl-6 space-y-4">
        {points.map((point, index) => (
          <li key={index}>
            {point}
          </li>
        ))}
      </ul>
    </SectionHeader>
  </CaseStudySection>
);


export const JuiceboxCaseStudy = () => {
  const title = "How Juicebox closes deals faster with the Wolfia AI Trust Center";
  const description = "Discover how Juicebox leverages the Wolfia AI Trust Center to accelerate their deals";
  const image = "/images/case-studies/juicebox_x_wolfia.png";

  return (

    <MarketingPage>
      <Meta title="Case Study - Juicebox">
        <meta property="og:title" content={title} />
        <meta property="og:image" content={image} />
        <meta property="og:description" content={description} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@wolfia_app" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
      </Meta>

      <CaseStudyHeaderSection />

      <CaseStudyCustomer
        quote="“Wolfia increased the speed of passing security review by 5x. AI compliance has been key for a number of our deals, and Wolfia has helped us turn compliance processes into a strength. In buyer conversations, I show the Wolfia Trust Center more than some of our product features. It’s a critical part of our sales process”"
        author="David Paffenholz"
        role="CEO & Co-Founder, Juicebox"
        image="/images/home/landingPage_testimonials_portrait_david.webp"
      />

      <div className="w-full pt-12 pb-12">
        <CaseStudyContentSection
          title="Challenge"
          points={[
            'Since the AI industry is new, several enterprise buyers are reluctant to buy AI products because of security and compliance concerns.',
            'Juicebox wanted to close enterprise deals - and needed to proactively showcase its AI security posture to its buyers and customers.'
          ]}
        />

        <CaseStudyContentSection
          title="Solution"
          points={[
            'Juicebox implemented the Wolfia AI Trust Center to document and share all of its AI security documentation and information.',
            'The Juicebox Trust Center showcases how it handles important AI security risks such as AI data security, AI model testing, AI Cybersecurity.',
            'Juicebox Trust Center also shows how it stays compliant with new AI compliance standards such as ISO 42001.'
          ]}
        />

        <CaseStudyContentSection
          title="Results"
          points={[
            'Juicebox was able to close enterprise deals significantly faster because of the Wolfia Trust Center.',
            'Buyers feel more confident that Juicebox is following robust AI security measures.',
            'In the case of one enterprise deal, the buyer had no follow-up security questions after seeing the Trust Center, saving the founder valuable time by not filling out annoying security questionnaires.',
            'The Trust Center got 5,000 views in just 2 weeks!',
          ]}
        />

        <CaseStudySection>
          <div
            className="m-6 text-center bg-gradient-to-br from-lupine-700 via-lupine-800 to-lupine-950 border border-lupine-300 border-opacity-10 p-12 rounded-lg drop-shadow-xl">
            <p className="font-sans text-lg md:text-xl text-plum-100 mb-12">
              Wolfia is the #1 AI compliance platform that helps AI companies close deals 10x faster. Our AI Trust Center
              Product allows you to showcase your AI security and compliance controls with buyers.
            </p>
            <h3 className="font-sans text-xl md:text-2xl text-plum-50 font-medium mb-12">
              Want to see how Wolfia can help you close deals faster?
            </h3>
            <a
              href="https://calendly.com/naren-mano/wolfia-demo"
              className="inline-flex text-plum-100 bg-plum-500 rounded-full items-center py-4 px-6 drop-shadow-xl hover:bg-plum-600 hover:text-plum-50 text-lg transition-all focus-visible:outline outline-plum-500 outline-offset-4 outline-4 font-semibold"
            >
              Schedule a demo
            </a>
          </div>
        </CaseStudySection>

      </div>
    </MarketingPage>
  );
};
